import { html, PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { BaseElement } from "../BaseElement";
import { styles } from "./FixedPanel.style";

/**
 * Panel with fixed header and footer. The body will scroll if it overflows vertically.
 */
@customElement("ds-fixed-panel")
export class FixedPanel extends BaseElement {
    static override styles = [styles];

    @property({ type: Boolean }) noFooter = false;

    @state() protected _hasHeader = false;
    @state() protected _hasBody = false;
    @state() protected _hasFooter = false;

    constructor() {
        super();
        // Observe changes in the light DOM
        const observer = new MutationObserver((mutationsList) => {
            for (const mutation of mutationsList) {
                if (mutation.type === "childList") {
                    this._updateSlots();
                }
            }
        });

        // Start observing the light DOM
        observer.observe(this as Node, { childList: true });
    }
    public override async connectedCallback(): Promise<void> {
        super.connectedCallback();
    }

    protected override firstUpdated(changedProperties: PropertyValues): void {
        super.firstUpdated(changedProperties);
        this._updateSlots();
    }

    private _updateSlots() {
        this._hasHeader = this._hasSlottedElements("header");
        this._hasBody = this._hasSlottedElements("body");
        this._hasFooter = this._hasSlottedElements("footer");
    }

    private _hasSlottedElements(slot: string) {
        const elem = this.shadowRoot?.querySelector(`slot[name="${slot}"]`) as HTMLSlotElement;
        return elem && elem.assignedNodes({ flatten: true }).length > 0;
    }

    private _getVisibleClass(hasElement: boolean) {
        return hasElement ? "visible" : "hidden";
    }

    override render() {
        return html` <div class="container">
            <div class="header ${this._getVisibleClass(this._hasHeader)}" part="header">
                <slot name="header"></slot>
            </div>
            <div class="body ${this._getVisibleClass(this._hasBody)}" part="body">
                <slot name="body"></slot>
            </div>

            <div class="footer ${this._getVisibleClass(this._hasFooter)}" part="footer">
                <slot name="footer"></slot>
            </div>
        </div>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-fixed-panel": FixedPanel;
    }
}
